'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { IPTVPlayer } from '@/components/IPTVPlayer';
import { PlaylistManager } from '@/components/PlaylistManager';
import { ChannelList } from '@/components/ChannelList';
import { Tv, Layout, Settings, List, Github, Trash2, X, CreditCard, Shield, Star, Lock, Zap, Volume2, VolumeX, Maximize2, ExternalLink } from 'lucide-react';
import { motion } from 'motion/react';
import * as parser from 'iptv-playlist-parser';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useGlobalPlayer } from '@/components/GlobalPlayerProvider';

interface Channel {
  name: string;
  url: string;
  tvg: {
    id: string;
    name: string;
    logo: string;
  };
  group: {
    title: string;
  };
}

interface Playlist {
  id: string;
  name: string;
  channels: Channel[];
  isDefault?: boolean;
}

export default function IPTVApp() {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState<'dashboard' | 'favorites' | 'playlists' | 'settings'>('dashboard');
  const [autoPlay, setAutoPlay] = useState(true);
  const [recordingQuality, setRecordingQuality] = useState<'low' | 'medium' | 'high'>('medium');
  const [favorites, setFavorites] = useState<{ url: string; name: string; logo: string }[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [user, setUser] = useState<any>(null);
  const [userPlan, setUserPlan] = useState<'free' | 'paid'>('free');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  
  // Multi-Screen Mode State
  const [multiScreen, setMultiScreen] = useState(false);
  const [multiScreenLayout, setMultiScreenLayout] = useState<'2' | '4'>('4');
  const [activeChannels, setActiveChannels] = useState<(Channel | null)[]>([null, null, null, null]);
  const [focusedSlot, setFocusedSlot] = useState(0);
  const [mutedSlots, setMutedSlots] = useState<boolean[]>([false, true, true, true]);
  const [showAppPromo, setShowAppPromo] = useState(true);
  const [adminAds, setAdminAds] = useState<any[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);

  const globalPlayer = useGlobalPlayer();

  // Fetch admin ads
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const res = await fetch('/api/mobile/ads?platform=web');
        const data = await res.json();
        if (data.success && data.ads) {
          setAdminAds(data.ads);
        }
      } catch (err) {
        console.error("Failed to load ads", err);
      }
    };
    fetchAds();
  }, []);

  // Ads auto-slider logic
  useEffect(() => {
    const sliderItems = ['default-promo', ...adminAds];
    if (sliderItems.length <= 1 || !showAppPromo) return;
    
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % sliderItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [adminAds, showAppPromo]);

  // Initialize from global player if returning from PiP
  useEffect(() => {
    if (globalPlayer?.playedChannel && !selectedChannel) {
      setSelectedChannel(globalPlayer.playedChannel as Channel);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync current video to global player for background PiP
  useEffect(() => {
    if (globalPlayer) {
      if (multiScreen) {
        globalPlayer.setPlayedChannel((activeChannels[focusedSlot] as any) || null);
      } else {
        globalPlayer.setPlayedChannel((selectedChannel as any) || null);
      }
    }
  }, [selectedChannel, multiScreen, activeChannels, focusedSlot, globalPlayer]);

  // Sync premium status to global player
  useEffect(() => {
    if (globalPlayer) {
      globalPlayer.setIsPremium(userPlan === 'paid');
    }
  }, [userPlan, globalPlayer]);

  // Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  // Plan & Favorites Sync
  useEffect(() => {
    if (!user) {
      setUserPlan('free');
      return;
    }

    const syncUserData = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          let currentPlan = data.plan || 'free';
          
          // Check for expiration
          if (currentPlan === 'paid' && data.premiumExpiresAt) {
            const expiryDate = data.premiumExpiresAt.toDate();
            if (new Date() > expiryDate) {
              currentPlan = 'free';
              // Update firestore to reflect expiration
              await setDoc(userRef, { plan: 'free', updatedBySystem: true }, { merge: true });
            }
          }
          
          setUserPlan(currentPlan);
          if (data.favorites) {
            // Migration: filter out old string-only favorites if they exist to prevent crashes
            const validFavs = Array.isArray(data.favorites) 
              ? data.favorites.filter(f => typeof f === 'object' && f !== null && f.url)
              : [];
            setFavorites(validFavs);
          }
        } else {
          await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            plan: 'free',
            favorites: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          setUserPlan('free');
        }
      } catch (error: any) {
        console.error("Error syncing user data:", error);
      }
    };

    syncUserData();
  }, [user]);

  // Push favorites to Firestore if paid user
  useEffect(() => {
    if (user && userPlan === 'paid') {
      const updateFavs = async () => {
        try {
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, { favorites, updatedAt: serverTimestamp() }, { merge: true });
        } catch (e) {
          console.error("Failed to sync favorites to cloud", e);
        }
      };
      
      const timeout = setTimeout(updateFavs, 1000); // Debounce sync
      return () => clearTimeout(timeout);
    }
  }, [favorites, user, userPlan]);

  const handleToggleFavorite = (channel: Channel) => {
    setFavorites(prev => {
      const exists = prev.some(f => f.url === channel.url);
      if (exists) {
        return prev.filter(f => f.url !== channel.url);
      } else {
        return [...prev, {
          url: channel.url,
          name: channel.name,
          logo: channel.tvg?.logo || ''
        }];
      }
    });
  };

  const handleLogin = async () => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        console.log("Login popup closed by user");
      } else if (error.code === 'auth/cancelled-popup-request') {
        console.log("Login request cancelled (likely multiple clicks)");
      } else {
        console.error("Login failed", error);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleAddPlaylist = useCallback((channels: any[], name: string, isDefault = false) => {
    const newPlaylist: Playlist = {
      id: isDefault ? 'default-iptv' : Date.now().toString(),
      name,
      isDefault,
      channels: channels.map(c => {
        // Infer Quality from name
        let quality: 'HD' | 'SD' | '4K' = 'SD';
        const nameLower = (c.name || '').toLowerCase();
        if (nameLower.includes('4k') || nameLower.includes('uhd')) quality = '4K';
        else if (nameLower.includes('hd') || nameLower.includes('fhd') || nameLower.includes('1080')) quality = 'HD';
        
        return {
          name: c.name || 'Unknown Channel',
          url: c.url,
          tvg: {
            id: c.tvg?.id || '',
            name: c.tvg?.name || '',
            logo: c.tvg?.logo || '',
          },
          group: {
            title: c.group?.title || 'General',
          },
          country: c.tvg?.country || '',
          language: c.tvg?.language || '',
          quality
        };
      }),
    };
    setPlaylists(prev => {
      if (isDefault && prev.some(p => p.id === 'default-iptv')) return prev;
      return [...prev, newPlaylist];
    });
  }, []);

  // Load playlists and settings from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Register Service Worker for PWA
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          navigator.serviceWorker.register('/sw.js').then(
            (registration) => {
              console.log('Service Worker registered with scope:', registration.scope);
            },
            (err) => {
              console.log('Service Worker registration failed:', err);
            }
          );
        });
      }

      const saved = localStorage.getItem('streamline_playlists');
      const savedSettings = localStorage.getItem('iptv_go_settings');
      const savedFavs = localStorage.getItem('iptv_go_favs');
      
      if (savedFavs) {
        try {
          const parsed = JSON.parse(savedFavs);
          // Migration: Ensure we only load objects
          const validFavs = Array.isArray(parsed) 
            ? parsed.filter(f => typeof f === 'object' && f !== null && f.url)
            : [];
          setFavorites(validFavs);
        } catch (e) {}
      }

      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.autoPlay !== undefined) {
            setTimeout(() => setAutoPlay(settings.autoPlay), 0);
          }
          if (settings.recordingQuality !== undefined) {
            setTimeout(() => setRecordingQuality(settings.recordingQuality), 0);
          }
          if (settings.viewMode !== undefined) {
            setTimeout(() => setViewMode(settings.viewMode), 0);
          }
        } catch (e) {
          console.error("Failed to load settings", e);
        }
      }

      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            const updated = parsed.map(p => {
              if (p.id === 'default-iptv') {
                return { ...p, name: 'IPTV-GO (Default)' };
              }
              return p;
            });
            setTimeout(() => {
              setPlaylists(updated);
            }, 0);
          }
        } catch (e) {
          console.error("Failed to load playlists", e);
        }
      }
    }
  }, []);

  // Add default playlist if not present
  useEffect(() => {
    const fetchDefaultPlaylist = async () => {
      if (playlists.some(p => p.id === 'default-iptv')) return;
      
      try {
        // Try fetching from our own managed channels first
        const token = user ? await user.getIdToken() : null;
        const apiRes = await fetch('/api/channels', {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (apiRes.ok) {
          const apiData = await apiRes.json();
          if (apiData.success && apiData.channels && apiData.channels.length > 0) {
            handleAddPlaylist(apiData.channels, 'IPTV-GO (Default)', true);
            return;
          }
        }

        // Fallback to external M3U if our database is empty or unauthorized
        const response = await fetch('https://iptv-org.github.io/iptv/index.m3u');
        const data = await response.text();
        const result = parser.parse(data);
        handleAddPlaylist(result.items, 'IPTV-GO (Default)', true);
      } catch (error) {
        console.error('Error fetching default playlist:', error);
      }
    };

    if (isAuthReady) {
      fetchDefaultPlaylist();
    }
  }, [playlists, handleAddPlaylist, user, isAuthReady]);

  // Save playlists and settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('streamline_playlists', JSON.stringify(playlists));
      localStorage.setItem('iptv_go_settings', JSON.stringify({ autoPlay, recordingQuality, viewMode }));
      localStorage.setItem('iptv_go_favs', JSON.stringify(favorites));
    } catch (e: any) {
      console.warn('Failed to save state locally. This might happen in Safari Private Browsing mode or if storage quota is exceeded.', e.message);
    }
  }, [playlists, autoPlay, recordingQuality, viewMode, favorites]);

  // Safety: Reset high quality if user is on free plan
  useEffect(() => {
    if (userPlan === 'free' && recordingQuality === 'high') {
      setRecordingQuality('medium');
    }
    if (userPlan === 'free' && viewMode === 'grid') {
      setViewMode('list');
    }
  }, [userPlan, recordingQuality, viewMode]);

  // Console Warning (Self-XSS protection)
  useEffect(() => {
    // Show security warning
    console.log(
      '%cStop!',
      'color: red; font-family: sans-serif; font-size: 4.5em; font-weight: bolder; text-shadow: #000 1px 1px;'
    );
    console.log(
      '%cThis is a browser feature intended for developers. If someone told you to copy-paste something here to enable a feature or "hack" someone\'s account, it\'s a scam and will give them access to your account.',
      'font-family: sans-serif; font-size: 1.5em; font-weight: bold;'
    );
    console.log(
      '%cSee https://en.wikipedia.org/wiki/Self-XSS for more information.',
      'font-family: sans-serif; font-size: 1.2em;'
    );
  }, []);

  const allChannels = playlists.flatMap(p => p.channels);

  return (
    <div className="flex h-screen h-[100dvh] bg-gray-950 text-gray-100 overflow-hidden font-sans">
      {/* Mini Sidebar / Navigation */}
      <div className="w-16 bg-gray-900 border-r border-gray-800 flex flex-col items-center py-6 gap-8 z-50">
        <Link href="/" className="w-10 h-10 bg-indigo-600 hover:bg-indigo-500 transition-colors rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 group cursor-pointer" title="Back to Home">
          <Tv size={24} className="text-white group-hover:scale-110 transition-transform" />
        </Link>
        
        <nav className="flex flex-col gap-6">
          <button 
            onClick={() => {
              if (activeView !== 'dashboard') {
                setActiveView('dashboard');
                setSidebarOpen(true);
              } else {
                setSidebarOpen(!sidebarOpen);
              }
            }}
            className={`p-2 rounded-lg transition-all cursor-pointer ${sidebarOpen && activeView === 'dashboard' ? 'text-indigo-400 bg-indigo-400/10' : 'text-gray-400 hover:text-gray-200'}`}
            title={sidebarOpen ? "Close Sidebar" : "Open Sidebar"}
            aria-label={sidebarOpen ? "Close Sidebar" : "Open Sidebar"}
          >
            <Layout size={20} />
          </button>
          <button 
            onClick={() => {
              setActiveView('favorites');
              setSidebarOpen(true);
            }}
            className={`p-2 rounded-lg transition-all cursor-pointer ${sidebarOpen && activeView === 'favorites' ? 'text-indigo-400 bg-indigo-400/10' : 'text-gray-400 hover:text-gray-200'}`}
            aria-label="Favorites"
            title="Favorites"
          >
            <Star size={20} className={activeView === 'favorites' ? 'fill-indigo-400' : ''} />
          </button>

          <button 
            onClick={() => {
              setActiveView('playlists');
              setSidebarOpen(true);
            }}
            className={`p-2 rounded-lg transition-all cursor-pointer ${sidebarOpen && activeView === 'playlists' ? 'text-indigo-400 bg-indigo-400/10' : 'text-gray-400 hover:text-gray-200'}`}
            aria-label="Playlists"
          >
            <List size={20} />
          </button>
          <button 
            onClick={() => {
              setActiveView('settings');
              setSidebarOpen(true);
            }}
            className={`p-2 rounded-lg transition-all cursor-pointer ${sidebarOpen && activeView === 'settings' ? 'text-indigo-400 bg-indigo-400/10' : 'text-gray-400 hover:text-gray-200'}`}
            aria-label="Settings"
          >
            <Settings size={20} />
          </button>
          <Link 
            href="/pricing"
            className="p-2 rounded-lg transition-all cursor-pointer text-gray-400 hover:text-indigo-400 hover:bg-indigo-400/10"
            aria-label="Pricing"
          >
            <CreditCard size={20} />
          </Link>
          {user?.email === 'robertnicjoo@gmail.com' && (
            <Link 
              href="/admin"
              className="p-2 rounded-lg transition-all cursor-pointer text-gray-400 hover:text-red-400 hover:bg-red-400/10"
              aria-label="Admin Panel"
              title="Admin Panel"
            >
              <Shield size={20} />
            </Link>
          )}
        </nav>

        <div className="mt-auto flex flex-col gap-6 pb-6">
          <div className="relative group">
            <a 
              href="https://play.google.com/store/apps/details?id=com.nicxonsolutions.iptv_go"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 block text-indigo-400 hover:text-indigo-300 transition-all cursor-pointer bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg relative overflow-hidden"
            >
              {/* CSS Fallback */}
              <div className="absolute inset-0 flex items-center justify-center bg-indigo-600/20">
                <Tv size={14} className="text-indigo-400/50" />
              </div>
              <img 
                src="/app-icon.png"
                alt="IPTV GO Android App" 
                width="20" 
                height="20" 
                draggable="false"
                className="w-5 h-5 object-contain relative z-10 block"
              />
            </a>
            
            {/* Tailwind Custom Tooltip */}
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-3 py-2 bg-gray-900 text-white text-xs font-bold whitespace-nowrap rounded-lg shadow-[0_0_20px_rgba(79,70,229,0.3)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-x-2 group-hover:translate-x-0 z-[100] border border-indigo-500/30 flex items-center gap-2">
              <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 border-l border-t border-indigo-500/30 -rotate-45" />
              <span className="text-indigo-400 uppercase tracking-widest text-[10px]">Get</span> Android App
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 flex overflow-hidden">
        {/* Channel List Sidebar */}
        <motion.div
          initial={false}
          animate={{ 
            width: sidebarOpen ? 340 : 0,
            opacity: sidebarOpen ? 1 : 0,
            visibility: sidebarOpen ? 'visible' : 'hidden'
          }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex flex-col h-full border-r border-gray-800 bg-gray-900/50 backdrop-blur-xl shrink-0 overflow-hidden"
        >
          {/* Sidebar Content Wrapper */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-6 pb-2 flex-shrink-0 flex items-center justify-between">
            <div className="flex flex-col">
              <h1 className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                IPTV GO
              </h1>
              {user && (
                <span className={`text-[10px] font-bold uppercase tracking-widest ${userPlan === 'paid' ? 'text-indigo-400' : 'text-gray-500'}`}>
                  {userPlan === 'paid' ? 'Premium Member' : 'Free Plan'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isAuthReady ? (
                <div className="w-12 h-6 bg-gray-800 rounded animate-pulse" />
              ) : !user ? (
                <button 
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-indigo-600 rounded hover:bg-indigo-700 transition-colors cursor-pointer ${isLoggingIn ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isLoggingIn ? '...' : 'Login'}
                </button>
              ) : (
                <button 
                  onClick={() => auth.signOut()}
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-gray-800 rounded hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  Logout
                </button>
              )}
              <button 
                onClick={() => setSidebarOpen(false)}
                className="text-gray-400 hover:text-white p-1 hover:bg-gray-800 rounded-lg transition-all cursor-pointer"
                title="Close Sidebar"
                aria-label="Close Sidebar"
              >
                <X size={20} />
              </button>
            </div>
          </div>

            <div className="flex-1 flex flex-col overflow-hidden">
              {activeView === 'dashboard' ? (
                <>
                  <div className="px-6 pb-4 flex-shrink-0">
                    <PlaylistManager onPlaylistAdded={handleAddPlaylist} />
                  </div>
                  <div className="flex-1 overflow-hidden flex flex-col border-t border-gray-800">
                    <ChannelList 
                      channels={allChannels} 
                      onChannelSelect={(c) => {
                        if (multiScreen) {
                          const newActive = [...activeChannels];
                          newActive[focusedSlot] = c;
                          setActiveChannels(newActive);
                          // Auto advance focus to next slot (looping)
                          const maxSlots = multiScreenLayout === '2' ? 2 : 4;
                          setFocusedSlot((focusedSlot + 1) % maxSlots);
                        } else {
                          setSelectedChannel(c);
                        }
                      }}
                      selectedChannelUrl={multiScreen ? activeChannels[focusedSlot]?.url : selectedChannel?.url}
                      favorites={favorites}
                      onToggleFavorite={handleToggleFavorite}
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      isPremium={userPlan === 'paid'}
                    />
                  </div>
                </>
              ) : activeView === 'favorites' ? (
                <div className="flex-1 overflow-hidden flex flex-col pt-4">
                  <div className="px-6 mb-4">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                       <Star size={20} className="text-yellow-500 fill-yellow-500" />
                       My Favorites
                    </h2>
                    <p className="text-xs text-gray-400 mt-1">Access your personalized channel collection with cloud sync.</p>
                  </div>
                  
                  <div className="flex-1 overflow-hidden flex flex-col border-t border-gray-800">
                    {userPlan === 'paid' ? (
                      <ChannelList 
                        channels={allChannels.filter(c => favorites.some(f => f.url === c.url))} 
                        onChannelSelect={(c) => {
                          if (multiScreen) {
                            const newActive = [...activeChannels];
                            newActive[focusedSlot] = c;
                            setActiveChannels(newActive);
                            const maxSlots = multiScreenLayout === '2' ? 2 : 4;
                            setFocusedSlot((focusedSlot + 1) % maxSlots);
                          } else {
                            setSelectedChannel(c);
                          }
                        }}
                        selectedChannelUrl={multiScreen ? activeChannels[focusedSlot]?.url : selectedChannel?.url}
                        favorites={favorites}
                        onToggleFavorite={handleToggleFavorite}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        isPremium={true}
                      />
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-900/50">
                        <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 border border-indigo-500/20 relative">
                           <Star size={40} className="text-indigo-400" />
                           <Lock size={16} className="absolute bottom-1 right-1 text-indigo-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">Cloud Favorites</h3>
                        <p className="text-sm text-gray-400 mb-8 max-w-[240px]">
                          Store your favorite channels in the cloud and access them instantly on all your devices.
                        </p>
                        <Link 
                          href="/pricing"
                          className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2 group"
                        >
                          <Zap size={18} className="fill-white" />
                          Unlock with Premium
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pb-6">
                  <div className="pt-2">
                    {activeView === 'playlists' && (
                      <div className="space-y-6">
                        <div className="space-y-4">
                          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Manage Playlists</h2>
                          <PlaylistManager onPlaylistAdded={handleAddPlaylist} />
                        </div>

                        <div className="space-y-4">
                          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Your Playlists</h2>
                          {playlists.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No playlists added yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {playlists.map(p => (
                                <div key={p.id} className={`p-3 rounded-lg border flex items-center justify-between group/item transition-all ${p.isDefault ? 'bg-indigo-600/10 border-indigo-500/30' : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'}`}>
                                  <div className="flex flex-col min-w-0">
                                    <span className="text-sm truncate pr-2 font-medium text-gray-200">{p.name}</span>
                                    {p.isDefault && <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider">System Default</span>}
                                  </div>
                                  {!p.isDefault && (
                                    <button 
                                      onClick={() => setPlaylists(prev => prev.filter(pl => pl.id !== p.id))}
                                      className="text-gray-400 hover:text-red-400 p-1.5 cursor-pointer transition-colors opacity-0 group-hover/item:opacity-100"
                                      title="Remove Playlist"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeView === 'settings' && (
                      <div className="space-y-6">
                        <div className="space-y-4">
                          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Player Settings</h2>
                          <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                            <span className="text-sm">Auto-play on selection</span>
                            <button 
                              onClick={() => setAutoPlay(!autoPlay)}
                              className={`w-10 h-5 rounded-full transition-colors relative ${autoPlay ? 'bg-indigo-600' : 'bg-gray-600'}`}
                            >
                              <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${autoPlay ? 'left-6' : 'left-1'}`} />
                            </button>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-400">Recording Quality</span>
                              {userPlan === 'free' && (
                                <Link href="/pricing" className="text-[10px] text-indigo-400 font-bold uppercase hover:underline">
                                  Upgrade for High Quality
                                </Link>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              {(['low', 'medium', 'high'] as const).map((q) => {
                                const isLocked = q === 'high' && userPlan === 'free';
                                return (
                                  <button
                                    key={q}
                                    onClick={() => !isLocked && setRecordingQuality(q)}
                                    disabled={isLocked}
                                    className={`py-2 px-3 rounded-lg text-xs font-medium border transition-all capitalize relative overflow-hidden ${
                                      recordingQuality === q 
                                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/20' 
                                        : isLocked
                                          ? 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed'
                                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                                    }`}
                                  >
                                    {q}
                                    {isLocked && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                        <Shield size={10} className="text-gray-500" />
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-[10px] text-gray-400 italic">
                              {userPlan === 'free' 
                                ? 'High quality recording is reserved for Premium members.' 
                                : 'Higher quality results in larger file sizes.'}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Data Management</h2>
                          <button 
                            onClick={() => {
                              if (confirm('Are you sure you want to clear all playlists?')) {
                                setPlaylists([]);
                              }
                            }}
                            className="w-full p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-all text-left"
                          >
                            Clear All Playlists
                          </button>
                          <button 
                            onClick={() => {
                              setPlaylists(prev => prev.filter(p => p.id !== 'default-iptv'));
                            }}
                            className="w-full p-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-all text-left"
                          >
                            Reset Default Playlist
                          </button>
                        </div>

                        <div className="pt-6 border-t border-gray-800">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                              <Tv size={20} className="text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white">IPTV GO</h3>
                              <p className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest">Forever Free</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 leading-relaxed">
                            A modern IPTV player built for performance and ease of use. 
                            Supports M3U and M3U8 playlists.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Sidebar Segment */}
          <div className="p-4 border-t border-gray-800 bg-gray-900/50 flex-shrink-0">
            {showAppPromo && (() => {
              const sliderItems = ['default-promo', ...adminAds];
              
              return (
                <div className="relative mb-6 group/promo h-[64px] w-full">
                  {sliderItems.map((item, idx) => {
                     const isDefault = item === 'default-promo';
                     const isActive = idx === currentAdIndex;

                     return (
                       <div 
                         key={isDefault ? 'default' : item.id} 
                         className={`absolute inset-0 transition-opacity duration-1000 ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                       >
                         {isDefault ? (
                           <a 
                             href="https://play.google.com/store/apps/details?id=com.nicxonsolutions.iptv_go"
                             target="_blank"
                             rel="noopener noreferrer"
                             className="flex items-center gap-3 p-3 bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all group shadow-lg shadow-indigo-600/20 w-full h-full"
                           >
                             <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative">
                               {/* CSS Fallback */}
                               <div className="absolute inset-0 flex items-center justify-center bg-indigo-100">
                                 <Tv size={20} className="text-indigo-400/50" />
                               </div>
                               <img 
                                 src="/app-icon.png"
                                 alt="IPTV GO" 
                                 width="40" 
                                 height="40" 
                                 draggable="false"
                                 className="w-full h-full object-cover relative z-10"
                               />
                             </div>
                             <div className="flex-1 min-w-0 pr-4">
                               <p className="text-xs font-bold text-white uppercase tracking-tight">Download App</p>
                               <p className="text-[10px] text-indigo-100 font-medium truncate">IPTV GO on Google Play</p>
                             </div>
                           </a>
                         ) : (
                           <a 
                             href={item.link || '#'}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all group shadow-lg border border-gray-700 overflow-hidden w-full h-full relative"
                           >
                             {item.image && (
                               <>
                                 <img src={item.image} alt={item.name} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-500" />
                                 <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-transparent" />
                               </>
                             )}
                             <div className="relative z-10 flex-1 min-w-0 pr-4">
                               <div className="text-[8px] font-black tracking-widest text-indigo-400 uppercase mb-0.5">Sponsored</div>
                               <p className="text-xs font-bold text-white uppercase tracking-tight truncate">{item.name}</p>
                             </div>
                           </a>
                         )}
                       </div>
                     );
                  })}

                  {/* Pager Dots */}
                  {sliderItems.length > 1 && (
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-20">
                      {sliderItems.map((_, i) => (
                        <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === currentAdIndex ? 'bg-indigo-500 w-3' : 'bg-gray-600'}`} />
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => setShowAppPromo(false)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-full border border-gray-700 shadow-md flex items-center justify-center transition-colors opacity-0 group-hover/promo:opacity-100 z-30"
                    title="Dismiss Promotion"
                    aria-label="Dismiss Promotion"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })()}

            {/* Essential Pages Links */}
            <div className={`flex flex-wrap justify-center gap-x-4 gap-y-2 px-2 ${showAppPromo ? 'border-t border-white/5 pt-6 mt-6' : 'pt-2'}`}>
              <Link href="/" className="text-[10px] font-medium text-gray-500 hover:text-indigo-400 transition-colors">Home</Link>
              <Link href="/about" className="text-[10px] font-medium text-gray-500 hover:text-indigo-400 transition-colors">About Us</Link>
              <Link href="/contact" className="text-[10px] font-medium text-gray-500 hover:text-indigo-400 transition-colors">Contact Us</Link>
              <Link href="/privacy" className="text-[10px] font-medium text-gray-500 hover:text-indigo-400 transition-colors">Privacy Policy</Link>
              <Link href="/terms" className="text-[10px] font-medium text-gray-500 hover:text-indigo-400 transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </motion.div>

        {/* Player Area */}
        <div className="flex-1 flex flex-col relative min-w-0 min-h-0">
          {/* Player Toolbar */}
          <div className="h-14 border-b border-gray-800 bg-gray-950/50 backdrop-blur-xl flex items-center justify-between px-6 flex-shrink-0 z-20">
             <div className="flex items-center gap-4">
                <button 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`p-2 rounded-lg transition-all ${!sidebarOpen ? 'text-indigo-400 bg-indigo-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                  title={sidebarOpen ? "Hide Sidebar" : "Show Sidebar"}
                >
                  <List size={20} />
                </button>
                {multiScreen && (
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full hidden md:flex">
                    <Zap size={12} className="text-indigo-400 fill-indigo-400" />
                    <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Multi-Screen Mode</span>
                  </div>
                )}
             </div>

             <div className="flex items-center gap-3">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-1 flex items-center shadow-inner">
                  <button
                    onClick={() => {
                      if (userPlan === 'paid') {
                        setMultiScreen(!multiScreen);
                      } else {
                        window.location.href = '/pricing';
                      }
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                      multiScreen 
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Layout size={14} className={multiScreen ? 'fill-white' : ''} />
                    Multi-Screen
                    {userPlan === 'free' && <Lock size={10} className="ml-0.5 opacity-50" />}
                  </button>
                  
                  {multiScreen && (
                    <div className="flex items-center gap-1 px-2 border-l border-gray-800 ml-1">
                      <button 
                        onClick={() => setMultiScreenLayout('2')}
                        className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${multiScreenLayout === '2' ? 'bg-gray-800 text-indigo-400' : 'text-gray-500 hover:text-gray-400'}`}
                      >
                        2x
                      </button>
                      <button 
                        onClick={() => setMultiScreenLayout('4')}
                        className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${multiScreenLayout === '4' ? 'bg-gray-800 text-indigo-400' : 'text-gray-500 hover:text-gray-400'}`}
                      >
                        4x
                      </button>
                    </div>
                  )}
                </div>
             </div>
          </div>

          <div className="flex-1 min-h-0 bg-[#080808] relative w-full flex flex-col">
            {multiScreen ? (
              <div className={`grid h-full ${multiScreenLayout === '2' ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'} gap-1 p-1 flex-1`}>
                {activeChannels.slice(0, multiScreenLayout === '2' ? 2 : 4).map((ch, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setFocusedSlot(idx)}
                    className={`relative w-full h-full bg-black/40 border-2 transition-all flex flex-col overflow-hidden multi-screen-zone ${
                      focusedSlot === idx ? 'border-indigo-500 z-10' : 'border-gray-800/50 hover:border-gray-700'
                    }`}
                  >
                     {ch ? (
                       <>
                         <div className="flex-1 min-h-0 bg-black">
                            <IPTVPlayer 
                              url={ch.url} 
                              title={ch.name}
                              autoPlay={autoPlay}
                              recordingQuality={recordingQuality}
                              hideControls={true}
                              muted={mutedSlots[idx]}
                              isPremium={userPlan === 'paid'}
                            />
                         </div>
                         <div className={`p-2 border-t border-gray-800 bg-black/80 flex items-center justify-between transition-all ${focusedSlot === idx ? 'bg-indigo-950/20' : 'opacity-60 group-hover/screen:opacity-100'}`}>
                           <div className="flex items-center gap-2 min-w-0 flex-1">
                             <div className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                             <span className="text-[10px] font-bold text-gray-300 truncate tracking-tight">{ch.name}</span>
                           </div>
                           <div className="flex items-center gap-1 ml-2">
                             <button 
                               onClick={async (e) => {
                                 e.stopPropagation();
                                 const zone = e.currentTarget.closest('.multi-screen-zone');
                                 const video = zone?.querySelector('video');
                                 if (video) {
                                   try {
                                     if (document.pictureInPictureElement) {
                                       await document.exitPictureInPicture();
                                     } else {
                                       await (video as any).requestPictureInPicture();
                                     }
                                   } catch (err) {
                                     console.error("PiP failed from grid", err);
                                   }
                                 }
                               }}
                               className="p-1.5 rounded text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
                               title="Picture in Picture"
                             >
                               <ExternalLink size={12} />
                             </button>
                             <button 
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const newMuted = [...mutedSlots];
                                 newMuted[idx] = !newMuted[idx];
                                 setMutedSlots(newMuted);
                               }}
                               className={`p-1.5 rounded transition-colors ${!mutedSlots[idx] ? 'text-indigo-400 bg-indigo-400/10' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                               title={mutedSlots[idx] ? "Unmute" : "Mute"}
                             >
                               {!mutedSlots[idx] ? <Volume2 size={12} /> : <VolumeX size={12} />}
                             </button>
                             <button 
                               onClick={(e) => {
                                 e.stopPropagation();
                                 setSelectedChannel(ch);
                                 setMultiScreen(false);
                               }}
                               className="p-1.5 rounded text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
                               title="Full Screen"
                             >
                               <Maximize2 size={12} />
                             </button>
                             <button 
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const newActive = [...activeChannels];
                                 newActive[idx] = null;
                                 setActiveChannels(newActive);
                               }}
                               className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                               title="Remove"
                             >
                               <Trash2 size={12} />
                             </button>
                           </div>
                         </div>
                       </>
                     ) : (
                       <div className="flex-1 flex flex-col items-center justify-center p-4">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 transition-colors ${focusedSlot === idx ? 'bg-indigo-500/20' : 'bg-gray-900'}`}>
                            <Tv className={focusedSlot === idx ? 'text-indigo-400' : 'text-gray-700'} size={24} />
                          </div>
                          <span className={`text-[10px] font-black uppercase tracking-widest ${focusedSlot === idx ? 'text-indigo-400' : 'text-gray-600'}`}>
                            {focusedSlot === idx ? 'Now Selecting...' : `Display Zone ${idx + 1}`}
                          </span>
                       </div>
                     )}
                     {focusedSlot === idx && (
                       <div className="absolute top-3 right-3 flex items-center gap-2">
                          <div className="px-2 py-0.5 bg-indigo-600 text-white text-[8px] font-black uppercase tracking-[0.1em] rounded shadow-lg">
                            Focused
                          </div>
                       </div>
                     )}
                  </div>
                ))}
              </div>
            ) : (
              <IPTVPlayer 
                url={selectedChannel?.url || ''} 
                title={selectedChannel?.name}
                autoPlay={autoPlay}
                recordingQuality={recordingQuality}
                isPremium={userPlan === 'paid'}
              />
            )}
          </div>

          {/* Player Footer / Info */}
          {(() => {
            const displayedChannel = multiScreen ? activeChannels[focusedSlot] : selectedChannel;
            if (!displayedChannel) return null;
            
            return (
              <div className="flex-shrink-0 p-4 md:p-6 bg-gray-900/90 backdrop-blur-xl border-t border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 z-10">
                <div className="flex items-center gap-4 max-w-full overflow-hidden">
                  <div className="w-12 h-12 bg-gray-800 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden border border-gray-700 relative shadow-inner">
                    <img 
                      src={displayedChannel.tvg.logo || '/default-channel.png'} 
                      alt="" 
                      loading="eager"
                      referrerPolicy="no-referrer"
                      draggable="false"
                      className="w-full h-full object-contain p-1" 
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/default-channel.png';
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-white truncate leading-tight">{displayedChannel.name}</h2>
                    <p className="text-sm text-gray-400 truncate mt-0.5">
                      {displayedChannel.group.title && displayedChannel.group.title.toLowerCase() !== 'undefined' ? displayedChannel.group.title : 'Live Stream'}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:items-end gap-2 w-full sm:w-auto">
                  <div className="flex items-center gap-2 px-3 py-1 bg-red-500/10 text-red-500 rounded-full text-[10px] font-bold uppercase tracking-widest border border-red-500/20 w-fit self-start sm:self-auto">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                    Live
                  </div>
                  {multiScreen && (
                    <div className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest px-2 py-0.5 bg-indigo-500/10 rounded border border-indigo-500/20 w-fit">
                      Zone {focusedSlot + 1} Focused
                    </div>
                  )}
                  <div className="text-[10px] text-gray-400 font-mono bg-black/40 p-2 rounded-md border border-white/5 overflow-x-auto max-w-full whitespace-nowrap custom-scrollbar shadow-inner">
                    {displayedChannel.url}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </main>
    </div>
  );
}
